import { useEffect, useRef, useState } from 'react';
import { QBox, useCurrentUser } from '@qualio/ui-components';
import { ContentEditor } from '../shared/ContentEditor/ContentEditor';
import { FORM_FIELD_TOOLBAR_ITEMS } from '../shared/CKEditor/config/ToolbarItems';
import { v4 } from 'uuid';
import { Editor } from 'ckeditor5';
import { useEditorUpload } from '../../context';

type RichTextInputProps = {
  value: string | null;
  onChange: (values: string) => void;
  onEditorReady?: (editor: Editor) => void;
  immediateOnChange?: boolean;
};

const RichTextInput = ({ value, onChange, onEditorReady, immediateOnChange }: RichTextInputProps) => {
  const { companyId } = useCurrentUser();
  const setEditorUploading = useEditorUpload()?.setEditorUploading;
  const editorIdRef = useRef(v4());
  const [editor, setEditor] = useState<Editor>();

  useEffect(() => {
    if (!editor || !setEditorUploading || !editor.plugins.has('FileRepository')) {
      return;
    }
    const editorId = editorIdRef.current;
    const { loaders } = editor.plugins.get('FileRepository');
    const handleLoadersChange = () => setEditorUploading(editorId, loaders.length > 0);
    loaders.on('change', handleLoadersChange);
    return () => {
      loaders.off('change', handleLoadersChange);
      setEditorUploading(editorId, false);
    };
  }, [editor, setEditorUploading]);

  const handleEditorReady = (readyEditor: Editor) => {
    setEditor(readyEditor);
    onEditorReady?.(readyEditor);
  };

  const handleAutoSave = async (editor: Editor) => {
    onChange(editor.getData());
  };

  const handleChange = immediateOnChange
    ? (_event: any, editor: Editor) => {
        onChange(editor.getData());
      }
    : undefined;

  return (
    <QBox pt={3}>
      <ContentEditor
        data={value ?? ''}
        companyId={companyId}
        handleAutoSave={handleAutoSave}
        onChange={handleChange}
        toolbarItems={FORM_FIELD_TOOLBAR_ITEMS}
        toolbarParentId={v4()}
        onEditorReady={handleEditorReady}
      />
    </QBox>
  );
};

export default RichTextInput;
